/**
 * Shared constants for the Flight Planner feature.
 * Single source of truth for drone models, templates, lenses, and workflow states.
 *
 * These are hardcoded defaults — flight templates can also be loaded from the API
 * at runtime (see FlightPlanner.jsx fetchTemplates).
 */

// ─── Workflow ─────────────────────────────────────────────────────────────────

export const WORKFLOW_STATES = {
	DRAWING: 'drawing',
	FLIGHT_TYPE: 'flight-type',
	DSP_CONFIG: 'dsp-config',
	SETUP: 'setup',
	EXPORT: 'export',
};

// ─── Drone models ─────────────────────────────────────────────────────────────
// Sources: DJI official spec sheets (enterprise.dji.com)
// - Mavic 3E: https://enterprise.dji.com/mavic-3-enterprise/specs
// - Matrice 4E: https://enterprise.dji.com/matrice-4-series/specs
//
// NOTE: focalLength is stored as 35mm equivalent (24mm). The code converts to
// actual focal length (~12mm) via crop factor in computeFlightPattern().
//
// TODO(verify-with-PO): minInterval — DJI specs say 0.5s (M4E) and 0.7s (M3E)
// for JPEG. Current value of 2s is conservative. Confirm intended behaviour:
// should we use DJI's minimum, or keep a safety margin?

export const DRONE_MODELS = [
	{
		id: 'dji-matrice-4-enterprise',
		name: 'DJI Matrice 4 Enterprise',
		camera: {
			sensorWidth: 17.3, // mm — standard 4/3" sensor (verified)
			sensorHeight: 13, // mm — standard 4/3" sensor (verified)
			focalLength: 24, // mm (35mm equiv; actual ~12mm via crop factor)
			imageWidth: 5280, // pixels (verified from DJI specs)
			imageHeight: 3956, // pixels (verified from DJI specs)
			minInterval: 2, // seconds — DJI spec: 0.5s (JPEG). See TODO above.
		},
	},
	{
		id: 'dji-mavic-3-enterprise',
		name: 'DJI Mavic 3 Enterprise',
		camera: {
			sensorWidth: 17.3, // mm — standard 4/3" sensor (verified)
			sensorHeight: 13, // mm — standard 4/3" sensor (verified)
			focalLength: 24, // mm (35mm equiv; actual ~12mm via crop factor)
			imageWidth: 5280, // pixels (verified from DJI specs)
			imageHeight: 3956, // pixels (verified from DJI specs)
			minInterval: 2, // seconds — DJI spec: 0.7s (JPEG). See TODO above.
		},
	},
];

// ─── DSP (Digital Sample Plot) ────────────────────────────────────────────────

// DSP Lens configurations with camera specs for altitude calculation
export const DSP_LENSES = {
	wide: {
		name: 'Wide-Angle',
		sensorWidth: 17.3, // mm
		focalLength: 12.0, // mm (actual focal length)
	},
	'3x-zoom': {
		name: '3x Zoom (Medium Tele)',
		sensorWidth: 9.8, // mm
		focalLength: 19.4, // mm
	},
	'7x-zoom': {
		name: '7x Zoom (Tele)',
		sensorWidth: 10.5, // mm
		focalLength: 50.0, // mm
	},
};

// DSP Templates with default area settings
export const DSP_TEMPLATES = {
	'sapling-count': {
		name: 'Sapling Count',
		description: 'Count young trees in sample plots',
		defaultArea: 15, // m²
		recommendedLens: 'wide',
	},
	'tree-health': {
		name: 'Tree Health Assessment',
		description: 'Detailed canopy analysis for health monitoring',
		defaultArea: 25, // m²
		recommendedLens: 'wide',
	},
	'species-identification': {
		name: 'Species Identification',
		description: 'Close-up imagery for species classification',
		defaultArea: 10, // m²
		recommendedLens: '3x-zoom',
	},
	'damage-assessment': {
		name: 'Damage Assessment',
		description: 'Inspect trees for pest or storm damage',
		defaultArea: 20, // m²
		recommendedLens: '3x-zoom',
	},
	'detailed-inspection': {
		name: 'Detailed Inspection',
		description: 'High-detail inspection of specific features',
		defaultArea: 5, // m²
		recommendedLens: '7x-zoom',
	},
	custom: {
		name: 'Custom',
		description: 'Configure your own DSP settings',
		defaultArea: 50, // m²
		recommendedLens: 'wide',
	},
};

// ─── Flight templates (orthomosaic) ───────────────────────────────────────────

export const DEFAULT_FLIGHT_TEMPLATES = {
	ungskogpleie: {
		name: 'Ungskogpleie',
		description: 'Young forest management - high detail for sapling assessment',
		requireMunicipality: true,
		settings: {
			flightHeight: 40,
			sideOverlap: 80,
			frontOverlap: 85,
			speed: 5,
			terrainFollow: true,
			smartOblique: true,
			gimbalPitch: -90,
			aiModelUuid: '',
		},
	},
	'storm-damage': {
		name: 'Storm Damage',
		description: 'Rapid assessment of storm-damaged forest areas',
		requireMunicipality: true,
		settings: {
			flightHeight: 100,
			sideOverlap: 70,
			frontOverlap: 80,
			speed: 10,
			terrainFollow: true,
			smartOblique: false,
			gimbalPitch: -90,
			aiModelUuid: '',
		},
	},
	standard: {
		name: 'Standard Survey',
		description: 'General mapping with balanced settings',
		requireMunicipality: true,
		settings: {
			flightHeight: 80,
			sideOverlap: 70,
			frontOverlap: 75,
			speed: 8,
			terrainFollow: false,
			smartOblique: false,
			gimbalPitch: -90,
			aiModelUuid: '',
		},
	},
	'high-precision': {
		name: 'High Precision',
		description: 'Detailed analysis with higher overlap',
		requireMunicipality: true,
		settings: {
			flightHeight: 50,
			sideOverlap: 80,
			frontOverlap: 85,
			speed: 5,
			terrainFollow: true,
			smartOblique: false,
			gimbalPitch: -90,
			aiModelUuid: '',
		},
	},
	'terrain-following': {
		name: 'Terrain Following',
		description: 'Maintains consistent height above ground',
		requireMunicipality: true,
		settings: {
			flightHeight: 60,
			sideOverlap: 75,
			frontOverlap: 80,
			speed: 6,
			terrainFollow: true,
			smartOblique: false,
			gimbalPitch: -90,
			aiModelUuid: '',
		},
	},
	'3d-mapping': {
		name: '3D Mapping',
		description: 'Captures oblique images for 3D models',
		requireMunicipality: true,
		settings: {
			flightHeight: 70,
			sideOverlap: 75,
			frontOverlap: 80,
			speed: 7,
			terrainFollow: false,
			smartOblique: true,
			gimbalPitch: -45,
			aiModelUuid: '',
		},
	},
};

// ─── Shared numeric constants ─────────────────────────────────────────────────

// Approximate flight time per battery in minutes (conservative estimate).
// DJI specs: M3E max 45 min flight / 38 min hover; M4E max 49 min / 42 min hover.
// Real-world mapping flights typically achieve 30–38 min.
// TODO(verify-with-PO): confirm 35 min aligns with field experience.
export const BATTERY_FLIGHT_TIME_MIN = 35;
